import { defineStore } from "pinia";
import {
  getProfile,
  loginApi,
  logoutApi,
  getCsrfToken,
  ssoLogin,
} from "../api/auth";
import { redirectToLogin } from "../utils/authRedirect";

const normalizePackageInfo = (packageInfo) => ({
  name: packageInfo?.name || "",
  storageLimit: Number(packageInfo?.storageLimit || 0),
  storageUnits: packageInfo?.storageUnits || "GB",
  usedSpace: Number(packageInfo?.usedSpace || 0),
  usedPercent: Number(packageInfo?.usedPercent || 0),
});

const buildProfileIconUrl = (profileIcon) =>
  profileIcon ? `/api/v1/download/${profileIcon}` : "";

const normalizeUserInfo = (data = {}) => {
  const hadBuyPackage = Boolean(
    data.hadBuyPackage ?? data.isSubscribed ?? data.isPurchased ?? false,
  );

  return {
    id: data.userId ?? data.id ?? null,
    userId: data.userId ?? data.id ?? null,
    fullName: data.fullName || data.nickName || data.name || "",
    name: data.fullName || data.nickName || data.name || "",
    email: data.emailAddress || data.email || "",
    emailAddress: data.emailAddress || data.email || "",
    profileIcon: data.profileIcon || "",
    avatarUrl:
      data.avatarUrl ||
      data.avatar ||
      data.headImg ||
      data.headImage ||
      data.profilePhoto ||
      buildProfileIconUrl(data.profileIcon) ||
      "",
    role: data.role || "USER",
    permissions: Array.isArray(data.permissions) ? data.permissions : [],
    orgId: data.orgId || "",
    status: data.status ?? "0",
    company: data.company || "",
    industry: data.industry || "",
    hadBuyPackage,
    isPurchased: hadBuyPackage,
    packageInfo: normalizePackageInfo(data.packageInfo),
  };
};

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null,
    loading: false,
    isSubscribed: false,
    isOrgAdmin: false,
    isBootstrappingAuth: false,
    authRedirectSuppressedCount: 0,
  }),
  getters: {
    isAuthed: (state) => Boolean(state.user?.userId || state.user?.id),
    shouldSuppressAuthRedirect: (state) => state.authRedirectSuppressedCount > 0,
  },
  actions: {
    clearAuthState() {
      this.user = null;
      this.isSubscribed = false;
      this.isOrgAdmin = false;
    },
    beginAuthRedirectSuppression() {
      this.authRedirectSuppressedCount += 1;
    },
    endAuthRedirectSuppression() {
      this.authRedirectSuppressedCount = Math.max(
        0,
        this.authRedirectSuppressedCount - 1,
      );
    },
    async runWithoutAuthRedirect(task) {
      this.beginAuthRedirectSuppression();
      try {
        return await task();
      } finally {
        this.endAuthRedirectSuppression();
      }
    },
    async login(payload) {
      this.loading = true;
      try {
        await this.getCfToken();
        await loginApi(payload);
        return await this.fetchProfile();
      } finally {
        this.loading = false;
      }
    },
    async bootstrapFromSid(sid) {
      if (!sid) {
        this.clearAuthState();
        return false;
      }

      this.isBootstrappingAuth = true;
      try {
        const response = await this.runWithoutAuthRedirect(() => ssoLogin(sid));
        if (typeof response?.code !== "undefined" && response.code !== 200) {
          this.clearAuthState();
          return false;
        }

        const ok = await this.fetchProfile({ shouldRedirectOnFail: false });
        if (!ok) {
          this.clearAuthState();
        }
        return ok;
      } catch (_error) {
        this.clearAuthState();
        return false;
      } finally {
        this.isBootstrappingAuth = false;
      }
    },
    async fetchProfile(options = {}) {
      const { shouldRedirectOnFail = true } = options;
      const executeRequest = shouldRedirectOnFail
        ? async (task) => task()
        : (task) => this.runWithoutAuthRedirect(task);

      try {
        const res = await executeRequest(async () => {
          await this.getCfToken();
          return getProfile();
        });

        if (res?.code === 401 || res?.code !== 200) {
          this.clearAuthState();
          if (shouldRedirectOnFail && res?.code === 401) {
            await redirectToLogin();
          }
          return false;
        }

        const nextUser = normalizeUserInfo(res?.data);
        if (!nextUser.userId) {
          this.clearAuthState();
          return false;
        }

        this.user = nextUser;
        this.isSubscribed = Boolean(nextUser.hadBuyPackage);
        this.isOrgAdmin = nextUser.role === "ORG_ADMIN";
        return true;
      } catch (error) {
        this.clearAuthState();
        if (shouldRedirectOnFail && error?.response?.status === 401) {
          await redirectToLogin();
        }
        return false;
      }
    },
    async getCfToken() {
      try {
        const csrfToken = await getCsrfToken();
        const csrf = csrfToken._csrf;
        sessionStorage.setItem("csrf", csrf);
      } catch (error) {
        console.error("Failed to get CSRF token:", error);
      }
    },
    async logout() {
      try {
        await logoutApi();
      } catch (_error) {
      } finally {
        this.clearAuthState();
      }
    },
    setTestState(type) {
      if (type === "visitor") {
        this.clearAuthState();
        return;
      }

      if (type === "trial") {
        this.user = normalizeUserInfo({
          userId: 888,
          fullName: "Trial User",
          emailAddress: "trial@example.com",
          role: "USER",
          hadBuyPackage: false,
          packageInfo: {
            name: "",
            storageLimit: 0,
            storageUnits: "GB",
            usedSpace: 0,
            usedPercent: 0,
          },
        });
        this.isSubscribed = false;
        this.isOrgAdmin = false;
        return;
      }

      if (type === "paid") {
        this.user = normalizeUserInfo({
          userId: 999,
          fullName: "Paid User",
          emailAddress: "paid@example.com",
          role: "USER",
          hadBuyPackage: true,
          packageInfo: {
            name: "Professional",
            storageLimit: 500,
            storageUnits: "GB",
            usedSpace: 325,
            usedPercent: 0.65,
          },
        });
        this.isSubscribed = true;
        this.isOrgAdmin = false;
        return;
      }

      if (type === "admin") {
        this.user = normalizeUserInfo({
          userId: 777,
          fullName: "Org Admin",
          emailAddress: "admin@example.com",
          role: "ORG_ADMIN",
          hadBuyPackage: true,
          packageInfo: {
            name: "Enterprise",
            storageLimit: 500,
            storageUnits: "GB",
            usedSpace: 325,
            usedPercent: 0.65,
          },
        });
        this.isSubscribed = true;
        this.isOrgAdmin = true;
      }
    },
  },
});
